import type { BrandStyle, MoodboardProfile } from '~/design-system/components/DeckGen/types/presentation'
import type { BrandKit } from './brandKit'

/**
 * The seam between the stored brand kit and the one the app works with.
 *
 * The backend treats `profile`, `tokens`, `sources` and `exampleUrls` as opaque TEXT: it stores and
 * returns JSON strings and never looks inside. That is deliberate (the client owns those shapes), and it
 * means this file is the ONLY place that knows what those strings contain. Parsing is defensive
 * throughout: a kit written by a newer client, or a row half-migrated by hand, must degrade to a kit with
 * fewer fields rather than throw and take the whole Brand Kits page down with it.
 *
 * ## What `tokens` actually holds
 *
 * A {@link BrandStyle} — the extractor's output — NOT a `BrandStyleTokens`. Tokens are a projection
 * result: `headingFontWeights` is derived from `fontFaces`, which only `BrandStyle` carries, so a kit
 * that stored tokens could never produce them. `brandKitToGenerationInputs` does that projection at
 * generation time.
 *
 * ## Why the logo is not in `tokens`
 *
 * `logoUrl` and `logoOnLight` are real columns, written by the upload / from-url / unlink routes. Keeping
 * a second copy inside `tokens` would guarantee one of them goes stale, and the stale one would be the
 * one generation reads. So the logo is stripped on the way in and the column is the single source.
 */

/** Two states only: the mark appears on every slide, or not at all. Mirrors the backend enum. */
export type LogoPlacement = 'EVERY_SLIDE' | 'OFF'

/** `BrandKitDto` as it arrives over the wire. The four payload fields are JSON strings. */
export interface BrandKitDto {
  id: string
  name: string
  profile: string | null
  tokens: string | null
  sources: string | null
  exampleUrls: string | null
  logoUrl: string | null
  logoOnLight: boolean | null
  logoPlacement: LogoPlacement
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

/** `BrandKitSummaryDto` — the list shape, without the payloads. */
export interface BrandKitSummaryDto {
  id: string
  name: string
  logoUrl: string | null
  logoPlacement: LogoPlacement
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

/** A stored kit, hydrated. Extends the generation-facing {@link BrandKit} with what only the page needs. */
export interface StoredBrandKit extends BrandKit {
  /** Our own stored copy of the mark. Authoritative: never read a logo out of `brandStyle`. */
  logoUrl?: string
  /** True when the mark reads on a LIGHT background. Chosen by clicking a swatch, not by answering. */
  logoOnLight?: boolean
  logoPlacement: LogoPlacement
  isDefault: boolean
}

/** The card-level shape the list endpoint can fill without shipping every payload. */
export interface BrandKitSummary {
  id: string
  name: string
  logoUrl?: string
  logoPlacement: LogoPlacement
  isDefault: boolean
  createdAt: number
  updatedAt: number
}

/**
 * A font family name, or undefined when what we were handed is not one.
 *
 * The CSS extractor sometimes returns a fragment of a shorthand rather than a family: stripe.com yields
 * `-0.01em` (letter-spacing) and anthropic.com yields `Clamp(1.125rem` (a `font:` shorthand split on the
 * comma INSIDE `clamp(...)`). Both are real, both observed on the running backend.
 *
 * That matters beyond a bad-looking card. The family is written into the generation prompt as
 * `font-family:'<name>'`, so `font-family:'Clamp(1.125rem'` is an instruction the model tries to follow.
 * A wrong font silently applied to every slide is worse than no font, so anything that reads as a length,
 * a number or a CSS function is dropped rather than passed on.
 *
 * Kopala owns the extractor fix; this stays regardless, because the prompt seam should not trust a
 * remote parser to be right.
 */
export function sanitizeBrandFont(raw: string | undefined | null): string | undefined {
  const name = raw?.trim().replace(/^['"]|['"]$/g, '').trim()
  if (!name) return undefined
  if (name.length > 64) return undefined
  // A CSS function fragment (`clamp(1.125rem`), a length (`-0.01em`, `16px`), or a bare number.
  if (/[(){};:]/.test(name)) return undefined
  if (/^-?[\d.]+(px|rem|em|%|vw|vh|pt|ch|ex)?$/i.test(name)) return undefined
  // A real family has at least one letter and does not start with a digit or a sign.
  if (!/[a-z]/i.test(name)) return undefined
  if (/^[-+.\d]/.test(name)) return undefined
  return name
}

/** JSON.parse that answers `undefined` instead of throwing. A malformed payload costs that one field. */
function parse<T>(raw: string | null | undefined, what: string): T | undefined {
  if (!raw) return undefined
  try {
    return JSON.parse(raw) as T
  } catch {
    console.warn(`[brandKit] ignoring unparseable ${what}`)
    return undefined
  }
}

/** An ISO timestamp to epoch ms. Falls back to 0 rather than NaN, which would render as "Invalid Date". */
function epoch(iso: string | null | undefined): number {
  if (!iso) return 0
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : 0
}

/** The empty profile a kit created before synthesis carries, so the rest of the app never sees undefined. */
const EMPTY_PROFILE: MoodboardProfile = {
  tasteProfile: '',
  keywords: [],
  avoids: [],
  guideline: '',
  palette: [],
}

/** Drop font fields that are CSS fragments rather than families. Applied on the way OUT of storage, so a
 *  kit written before the guard existed is cleaned too: those already exist, and generation reads them. */
function withCleanFonts<T extends { headingFont?: string; bodyFont?: string }>(value: T): T {
  const headingFont = sanitizeBrandFont(value.headingFont)
  const bodyFont = sanitizeBrandFont(value.bodyFont)
  if (headingFont === value.headingFont && bodyFont === value.bodyFont) return value
  const next = { ...value }
  if (headingFont) next.headingFont = headingFont
  else delete next.headingFont
  if (bodyFont) next.bodyFont = bodyFont
  else delete next.bodyFont
  return next
}

export function brandKitFromDto(dto: BrandKitDto): StoredBrandKit {
  const profile = parse<MoodboardProfile>(dto.profile, 'profile')
  const brandStyle = parse<BrandStyle>(dto.tokens, 'tokens')
  const sources = parse<BrandKit['sources']>(dto.sources, 'sources')
  return {
    id: dto.id,
    name: dto.name,
    profile: profile ? withCleanFonts(profile) : EMPTY_PROFILE,
    // Strip any logo a older/newer writer left in here: the column wins, always. Without this a kit whose
    // logo was replaced would generate with the previous mark, which is exactly the stale-copy failure
    // keeping the logo out of `tokens` is meant to prevent.
    ...(brandStyle ? { brandStyle: withCleanFonts(stripLogo(brandStyle)) } : {}),
    ...(sources ? { sources } : {}),
    examples: parse<string[]>(dto.exampleUrls, 'exampleUrls') ?? [],
    ...(dto.logoUrl ? { logoUrl: dto.logoUrl } : {}),
    ...(dto.logoOnLight != null ? { logoOnLight: dto.logoOnLight } : {}),
    logoPlacement: dto.logoPlacement ?? 'EVERY_SLIDE',
    isDefault: !!dto.isDefault,
    createdAt: epoch(dto.createdAt),
    updatedAt: epoch(dto.updatedAt),
  }
}

/**
 * The kit's mark, when there is one and the user has not turned it off.
 *
 * ONE definition, because two consumers must agree or the mark is worse than absent. The theme recipe
 * decides whether to TELL the model about a logo (`withStoredMark` in styleImport); the image allow list
 * decides whether an `<img>` pointing at it SURVIVES post-processing (`stripUnlistedImages`). If those
 * two ever disagree, the model is asked to place a file that is then deleted from its output — which is
 * indistinguishable, from the outside, from the model ignoring the instruction.
 *
 * Reads the column and never `brandStyle.logoUrl`, which `stripLogo` deletes on the way in.
 */
export function kitMarkUrl(
  kit: Pick<StoredBrandKit, 'logoUrl' | 'logoPlacement'> | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string | undefined {
  if (!kit || kit.logoPlacement === 'OFF') return undefined
  const url = kit.logoUrl?.trim()
  if (!url) return undefined
  // Absolute, for the same reason `kitSite.absoluteAssetUrl` is: a root-relative URL has nothing to
  // resolve against in a document that is not served from an origin. Storage is permitAll, so no cookie
  // is needed.
  //
  // THIS IS NOT WHAT MAKES THE MARK RENDER, and it is worth saying so plainly because it looks like it
  // should be. Measured 2026-09-04 in the composition stage's own `sandbox="allow-scripts"` frame: the
  // relative URL fails, the absolute URL fails, an absolute URL to an unrelated public CDN fails, and a
  // `data:` URI loads. No remote image resolves in an opaque-origin document at all, which is also why
  // the artifact contract bans them. `inlineKitMark` is the thing that makes the logo appear.
  //
  // UNRESOLVED, AND SOMEBODY SHOULD SETTLE IT: `styleRender.ts` states the opposite as measured fact —
  // that a sandbox without `allow-same-origin` blocks fetch but never blocked an `<img>`, which is what
  // the platform documents — and the shipped `applyVerifiedLogos` pass writes remote srcs into these same
  // documents on that basis. Both cannot be true, and the codebase currently acts on both. Whichever way
  // it falls, `inlineKitMark` is safe: a `data:` URI renders under either reading. What turns on it is
  // whether that pass is load-bearing or merely belt-and-braces, so re-measure and delete the loser.
  if (/^(https?:|data:)/i.test(url) || !origin) return url
  const base = origin.replace(/\/$/, '')
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`
}

/**
 * EVERY mark this kit could legitimately place, absolute, for the image allow list and the inline pass.
 *
 * `kitMarkUrl` answers "the active one", which was the right question while the recipe always named the
 * active one. It no longer does: `styleFromBrandKit` now picks the variation whose ground matches the
 * theme, so the document can reference any of them. A filter built from the active mark alone would
 * strip the very file the recipe just asked for, and an inline pass looking only for the active URL would
 * leave the chosen one as a dead link — which renders as a broken-image icon in the artifact frame.
 *
 * So both sides work off the whole set. It is at most a handful of URLs, they are all this kit's own
 * stored assets, and none of them is a URL the model composed.
 */
export function kitMarkUrls(
  kit: (Pick<StoredBrandKit, 'logoUrl' | 'logoPlacement'> & { profile?: unknown }) | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string[] {
  if (!kit || kit.logoPlacement === 'OFF') return []
  const out = new Set<string>()
  const add = (raw: string | undefined) => {
    const url = kitMarkUrl({ logoUrl: raw, logoPlacement: kit.logoPlacement }, origin)
    if (url) out.add(url)
  }
  add(kit.logoUrl)
  const variations = (kit.profile as { logoVariations?: { logoUrl?: string }[] } | undefined)?.logoVariations
  for (const v of variations ?? []) add(v.logoUrl)
  return [...out]
}

/** `BrandStyle` without its `logoUrl`. See the header: the column is the single source for the mark. */
function stripLogo(style: BrandStyle): BrandStyle {
  if (!style.logoUrl) return style
  const rest = { ...style }
  delete rest.logoUrl
  return rest
}

export function brandKitSummaryFromDto(dto: BrandKitSummaryDto): BrandKitSummary {
  return {
    id: dto.id,
    name: dto.name,
    ...(dto.logoUrl ? { logoUrl: dto.logoUrl } : {}),
    logoPlacement: dto.logoPlacement ?? 'EVERY_SLIDE',
    isDefault: !!dto.isDefault,
    createdAt: epoch(dto.createdAt),
    updatedAt: epoch(dto.updatedAt),
  }
}

/** The PATCH body for a kit edit. Every field is optional and `null` is never sent: on the backend a
 *  null field means "leave alone", so omitting is the same thing and says it more clearly. */
export interface BrandKitPatch {
  name?: string
  profile?: string
  tokens?: string
  sources?: string
  exampleUrls?: string
  logoPlacement?: LogoPlacement
  logoOnLight?: boolean
}

/**
 * `exampleUrls` holds STORAGE URLs and never base64. The column is documented that way in V93, and the
 * reason is the whole point of moving examples out of the deck blob: an inline example is 100-250KB, and
 * a user-scoped table accumulates them across every kit instead of dying with one deck.
 *
 * The type cannot express that, so the guard is here. A data URL is dropped rather than written, because
 * the alternative is a Brand Kits page that gets slower every time someone saves a kit and a cause nobody
 * finds for months. Losing a decorative thumbnail is the cheaper failure by a wide margin.
 */
function storageUrlsOnly(examples: string[]): string[] {
  const kept = examples.filter((url) => typeof url === 'string' && !url.trim().toLowerCase().startsWith('data:'))
  if (kept.length !== examples.length) {
    console.warn('[brandKit] dropped inline example image(s): exampleUrls stores URLs, never base64')
  }
  return kept
}

/** Serialize an edit. Only the fields present on `patch` are sent, so a rename cannot blank a logo that
 *  a concurrent upload just attached. */
export function brandKitToPatch(patch: {
  name?: string
  profile?: MoodboardProfile
  brandStyle?: BrandStyle
  sources?: BrandKit['sources']
  examples?: string[]
  logoPlacement?: LogoPlacement
  logoOnLight?: boolean
}): BrandKitPatch {
  return {
    ...(patch.name !== undefined ? { name: patch.name } : {}),
    ...(patch.profile !== undefined ? { profile: JSON.stringify(patch.profile) } : {}),
    ...(patch.brandStyle !== undefined ? { tokens: JSON.stringify(stripLogo(patch.brandStyle)) } : {}),
    ...(patch.sources !== undefined ? { sources: JSON.stringify(patch.sources) } : {}),
    ...(patch.examples !== undefined ? { exampleUrls: JSON.stringify(storageUrlsOnly(patch.examples)) } : {}),
    ...(patch.logoPlacement !== undefined ? { logoPlacement: patch.logoPlacement } : {}),
    ...(patch.logoOnLight !== undefined ? { logoOnLight: patch.logoOnLight } : {}),
  }
}

/**
 * An Error carrying the SERVER's reason when it gave one.
 *
 * A refused logo is usually the user's problem and the backend already words it for them, e.g. a favicon
 * is 16x16 and looks broken stretched across a slide, upload the real file. Throwing "logo failed: 422"
 * instead would replace an answer with a status code, which is what makes people file the wrong bug.
 *
 * The body shape is not guaranteed (a plain string, `{message}`, `{error}`, or Spring's error envelope),
 * so every form is tried and anything unrecognisable falls back to the status. A 5xx is deliberately NOT
 * shown: that is our fault, not something to word at the user.
 */
export class BrandLogoError extends Error {
  /** The server's own sentence, when it gave one worth showing. */
  readonly reason?: string
  constructor(status: number, reason?: string) {
    super(reason ?? `logo request failed: ${status}`)
    this.name = 'BrandLogoError'
    if (reason) this.reason = reason
  }
}

export async function logoError(response: Response): Promise<BrandLogoError> {
  if (response.status >= 500) return new BrandLogoError(response.status)
  const raw = await response.text().catch(() => '')
  if (!raw) return new BrandLogoError(response.status)
  let reason = raw.trim()
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed === 'string') reason = parsed
    else if (parsed && typeof parsed === 'object') {
      const body = parsed as Record<string, unknown>
      const found = [body.message, body.error, body.detail].find((v) => typeof v === 'string' && v.trim())
      reason = typeof found === 'string' ? found.trim() : ''
    }
  } catch {
    /* not JSON: the raw text is the message */
  }
  // A bare status word ("Unprocessable Entity") is not a reason, it is the status spelled out.
  if (!reason || reason.length < 12 || /^[A-Z][a-z]+( [A-Z][a-z]+)*$/.test(reason)) {
    return new BrandLogoError(response.status)
  }
  return new BrandLogoError(response.status, reason)
}
